/**
 * Count a callable's 4xx HTTP rejection that nothing else reports.
 *
 * THE GAP THIS CLOSES (docs/ops 2026-09-14 "expected server rejections produce zero telemetry"). ISS-460: every
 * schedule-block Update was refused by the server for four days and no signal fired, because the network interceptor
 * reports only status 0 and 5xx. `reportCallableRejection` (#4758) fixed that for the call sites that opted in: 7 callables
 * out of ~500 `httpsCallable` call sites in the client. The other callables stayed silent.
 *
 * This is the same signal at the one place every callable passes through (the fetch wrapper -> handleNetworkError):
 * one `warning` per callable + HTTP status, fingerprinted `["callable-http-rejection", callable, status]`, so each
 * pair is ONE Sentry issue whose event count rises with the failure rate. A callable failing for everyone then shows as a
 * climbing issue instead of silence.
 *
 * WHAT IT SENDS: the callable name (the path of our own functions host, never the query) and the HTTP status. NEVER the
 * response body or the server's message (both routinely hold customer names and addresses) and never the payload.
 * The page URL the SDK stamps on the event is replaced by the de-identified route, and the breadcrumbs (full fetch/navigation URLs) are cleared.
 *
 * NO DOUBLE COUNT: a callable named in REPORTED_BY_CALL_SITE already reports each rejection with its own code and payload
 * shape; this path skips it. A test scans the source so the list cannot drift from the real call sites.
 *
 * NAMED LIMITS: (1) only the main `client` app wires the interceptor; client-tech, sales-client and time-client do not.
 * (2) HTTP 401 and 403 are not counted (the interceptor treats them as expected auth redirects). (3) The status
 * distinguishes 400 (invalid-argument / failed-precondition / out-of-range) from 404, 409 and 429, not the exact
 * callable code, because the body is deliberately not read. (4) A callable in REPORTED_BY_CALL_SITE is skipped here even for a code its own reporter ignores or a call site that swallows the error: a deliberate double-count guard, so that gap stays. (5) The cooldown is per browser tab, so a burst in one tab is one
 * event per window; many users still add up in Sentry.
 */
import * as Sentry from '@sentry/react';
import { addScrubbedRequestProcessor, currentRoute } from './scrubUrl';
import { REPORTED_BY_CALL_SITE } from './reportCallableRejection';

/** One event per callable + status per tab per window, so a retry loop cannot flood Sentry. */
export const HTTP_REJECTION_COOLDOWN_MS = 30_000;

const lastSentAt = new Map<string, number>();

export function __resetHttpRejectionCooldownForTests(): void {
  lastSentAt.clear();
}

/** Returns true when an event was sent. Never throws: telemetry must not break the path it observes. */
export function reportCallableHttpRejection(callableName: string, status: number, now: number = Date.now()): boolean {
  if (status < 400 || status >= 500) return false;
  if (REPORTED_BY_CALL_SITE.has(callableName)) return false;
  const key = `${callableName}:${status}`;
  const last = lastSentAt.get(key);
  if (last !== undefined && now - last < HTTP_REJECTION_COOLDOWN_MS) return false;
  try {
    lastSentAt.set(key, now);
    Sentry.withScope((scope) => {
      scope.setLevel('warning');
      scope.setFingerprint(['callable-http-rejection', callableName, String(status)]);
      scope.setTags({ callable: callableName, http_status: String(status), route: currentRoute() });
      addScrubbedRequestProcessor(scope, { clearBreadcrumbs: true }); // breadcrumbs hold full fetch/navigation URLs with ids and query strings
      Sentry.captureMessage(`Callable rejected: ${callableName} (HTTP ${status})`);
    });
    return true;
  } catch {
    return false;
  }
}
